import { Physics, CollisionPair } from './Physics'
import { Renderer } from './Renderer'
import { InputHandler } from './InputHandler'
import { Fruit, getFruitFromBody } from './Fruit'
import { FruitType, getRandomSpawnableFruit, getNextFruitType } from './FruitTypes'

export interface GameConfig {
  canvas: HTMLCanvasElement
  onScoreChange: (score: number) => void
  onNextFruitChange: (fruit: FruitType) => void
  onGameOver: (finalScore: number) => void
}

export class Game {
  private physics: Physics
  private renderer: Renderer
  private input: InputHandler
  private fruits: Fruit[] = []
  private currentFruit: FruitType
  private nextFruit: FruitType
  private score: number = 0
  private isGameOver: boolean = false
  private canDrop: boolean = true
  private dropCooldown: number = 500 // ms between drops
  private lastDropTime: number = 0
  private gameOverCheckDelay: number = 1000 // ms after drop to check game over
  private config: GameConfig
  private animationId: number | null = null
  private lastTime: number = 0

  // Dimensions
  private readonly BOUNDARY_LINE_Y = 100
  private readonly WALL_THICKNESS = 50

  constructor(config: GameConfig) {
    this.config = config

    const rect = config.canvas.getBoundingClientRect()
    const width = rect.width
    // Use the smaller of canvas height and visible viewport to account for browser chrome
    const visibleHeight = window.visualViewport?.height || window.innerHeight
    const height = Math.min(rect.height, visibleHeight - 80) // 80px buffer for browser UI

    this.physics = new Physics({
      width,
      height,
      wallThickness: this.WALL_THICKNESS,
      boundaryLineY: this.BOUNDARY_LINE_Y
    })

    this.renderer = new Renderer({
      canvas: config.canvas,
      boundaryLineY: this.BOUNDARY_LINE_Y
    })

    this.input = new InputHandler(config.canvas)

    // Initialize fruits
    this.currentFruit = getRandomSpawnableFruit()
    this.nextFruit = getRandomSpawnableFruit()
    this.config.onNextFruitChange(this.nextFruit)

    this.setupInputHandlers()
    this.setupCollisionHandlers()
  }

  private setupInputHandlers(): void {
    this.input.onDrop((x) => {
      if (this.canDrop && !this.isGameOver) {
        this.dropFruit(x)
      }
    })
  }

  private setupCollisionHandlers(): void {
    this.physics.onCollision((pairs: CollisionPair[]) => {
      this.handleCollisions(pairs)
    })
  }

  private handleCollisions(pairs: CollisionPair[]): void {
    const processedPairs = new Set<string>()

    for (const pair of pairs) {
      const fruitA = getFruitFromBody(pair.bodyA)
      const fruitB = getFruitFromBody(pair.bodyB)

      if (!fruitA || !fruitB) continue
      if (fruitA.markedForRemoval || fruitB.markedForRemoval) continue

      // Create unique pair key
      const pairKey = [fruitA.id, fruitB.id].sort().join('-')
      if (processedPairs.has(pairKey)) continue
      processedPairs.add(pairKey)

      // Check if same type
      if (fruitA.type.id === fruitB.type.id) {
        this.mergeFruits(fruitA, fruitB)
      }
    }
  }

  private mergeFruits(fruitA: Fruit, fruitB: Fruit): void {
    const nextType = getNextFruitType(fruitA.type.id)

    // Calculate midpoint
    const midX = (fruitA.x + fruitB.x) / 2
    const midY = (fruitA.y + fruitB.y) / 2

    // Remove both fruits
    fruitA.remove(this.physics)
    fruitB.remove(this.physics)

    // Remove from array
    this.fruits = this.fruits.filter(
      f => f.id !== fruitA.id && f.id !== fruitB.id
    )

    // Add score
    this.addScore(fruitA.type.score + fruitB.type.score)

    // Create new fruit if not at max
    if (nextType) {
      const newFruit = new Fruit(nextType, midX, midY, this.physics, false)
      this.fruits.push(newFruit)
    }
  }

  private dropFruit(x: number): void {
    const now = Date.now()
    if (now - this.lastDropTime < this.dropCooldown) return

    this.canDrop = false
    this.lastDropTime = now

    // Clamp x to keep fruit inside walls
    const clampedX = this.input.clampX(x, this.currentFruit.radius + 5)

    // Create and drop fruit
    const fruit = new Fruit(
      this.currentFruit,
      clampedX,
      this.BOUNDARY_LINE_Y - 20,
      this.physics,
      false
    )
    this.fruits.push(fruit)
    fruit.drop(this.physics)

    // Prepare next fruit
    this.currentFruit = this.nextFruit
    this.nextFruit = getRandomSpawnableFruit()
    this.config.onNextFruitChange(this.nextFruit)

    // Allow dropping again after cooldown
    setTimeout(() => {
      this.canDrop = true
    }, this.dropCooldown)

    // Check for game over after delay
    setTimeout(() => {
      this.checkGameOver()
    }, this.gameOverCheckDelay)
  }

  private addScore(points: number): void {
    this.score += points
    this.config.onScoreChange(this.score)
  }

  private checkGameOver(): void {
    if (this.isGameOver) return

    for (const fruit of this.fruits) {
      if (fruit.isDropped && fruit.hasSettled() && fruit.isAboveLine(this.BOUNDARY_LINE_Y)) {
        this.gameOver()
        return
      }
    }
  }

  private gameOver(): void {
    this.isGameOver = true
    this.canDrop = false
    this.input.setEnabled(false)
    this.config.onGameOver(this.score)
  }

  start(): void {
    this.lastTime = performance.now()
    this.gameLoop()
  }

  private gameLoop(): void {
    const currentTime = performance.now()
    const deltaTime = currentTime - this.lastTime
    this.lastTime = currentTime

    // Update physics
    this.physics.update(deltaTime)

    // Continuous game over check for settled fruits
    if (!this.isGameOver && this.canDrop) {
      this.checkGameOver()
    }

    // Render
    const mouseX = this.input.getX()
    const clampedX = mouseX !== null
      ? this.input.clampX(mouseX, this.currentFruit.radius + 5)
      : null

    this.renderer.render(
      this.fruits,
      clampedX,
      this.currentFruit,
      this.canDrop && !this.isGameOver
    )

    // Continue loop
    this.animationId = requestAnimationFrame(() => this.gameLoop())
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  restart(): void {
    // Clear physics
    this.physics.clear()

    // Reset state
    this.fruits = []
    this.score = 0
    this.isGameOver = false
    this.canDrop = true
    this.lastDropTime = 0

    // Get new fruits
    this.currentFruit = getRandomSpawnableFruit()
    this.nextFruit = getRandomSpawnableFruit()

    // Update UI
    this.config.onScoreChange(0)
    this.config.onNextFruitChange(this.nextFruit)

    // Re-enable input
    this.input.setEnabled(true)
  }

  resize(): void {
    this.renderer.resize()
  }
}
